import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../Component/Header";
import { supabase } from "../supabase/client";
import { userErrorMessage } from "../utils/userErrorMessage";

const planLabels = { trial: "한 달 무료", monthly: "월간 선불", annual: "연간 선불" };
const statusLabels = { pending: "결제 대기", trial_pending: "무료체험 승인 대기", trial: "무료체험 중", paid: "이용 중", cancelled: "해지됨", failed: "실패" };
const paymentStatusLabels = { pending: "결제 대기", paid: "결제 완료", failed: "결제 실패" };
const won = (value) => `${Number(value || 0).toLocaleString("ko-KR")}원`;
const formatDate = (value, withTime = false) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("ko-KR", {
    timeZone: "Asia/Seoul", year: "numeric", month: "2-digit", day: "2-digit",
    ...(withTime ? { hour: "2-digit", minute: "2-digit", hour12: false } : {}),
  });
};

async function getFunctionError(error) {
  try {
    const response = error?.context?.clone
      ? await error.context.clone().json()
      : null;
    return userErrorMessage(response?.error || error);
  } catch {
    return userErrorMessage(error);
  }
}

export default function MyAccount() {
  const [data, setData] = useState({ profile: null, subscription: null, payments: [] });
  const [form, setForm] = useState({ driverName: "", phone: "", position: "", branch: "" });
  const [targetPlan, setTargetPlan] = useState("monthly");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const loadAccount = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const { data: result, error } = await supabase.functions.invoke("account-subscription", {
        body: { action: "get" },
      });
      if (error) throw error;
      const profile = result?.profile || null;
      setData({ profile, subscription: result?.subscription || null, payments: result?.payments || [] });
      setForm({
        driverName: profile?.driver_name || "", phone: profile?.phone || "",
        position: profile?.position || "", branch: profile?.branch || "",
      });
      setTargetPlan(result?.subscription?.pending_plan ||
        (result?.subscription?.plan === "monthly" ? "annual" : "monthly"));
    } catch (error) {
      setErrorMessage(await getFunctionError(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAccount(); }, [loadAccount]);
  const subscription = data.subscription;
  const active = subscription?.status === "paid";
  const changeSummary = useMemo(() => {
    if (!active || targetPlan === subscription.plan) return "현재 요금제를 유지합니다.";
    const amount = targetPlan === "annual" ? 300000 : 29900;
    return `${formatDate(subscription.access_end_at || subscription.service_end_at)}에 ${planLabels[targetPlan]} 요금제로 변경되고 ${won(amount)}이 결제됩니다.`;
  }, [active, subscription, targetPlan]);

  const invoke = async (action, body = {}) => {
    setBusy(action);
    setMessage("");
    setErrorMessage("");
    try {
      const { data: result, error } = await supabase.functions.invoke("account-subscription", {
        body: { action, ...body },
      });
      if (error) throw error;
      return result;
    } catch (error) {
      setErrorMessage(await getFunctionError(error));
      return null;
    } finally {
      setBusy("");
    }
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    const result = await invoke("update_profile", form);
    if (!result?.ok) return;
    localStorage.setItem("userName", result.profile.driver_name);
    window.dispatchEvent(new CustomEvent("account-profile-updated", {
      detail: { driverName: result.profile.driver_name },
    }));
    setMessage("계정 정보가 수정되었습니다.");
    await loadAccount();
  };

  const changePlan = async () => {
    const verb = targetPlan === subscription?.plan ? "예약된 요금제 변경을 취소" : `${planLabels[targetPlan]}으로 변경 예약`;
    if (!window.confirm(`${verb}하시겠습니까?\n\n${changeSummary}`)) return;
    const result = await invoke("change_plan", { targetPlan });
    if (!result?.ok) return;
    setMessage(result.cleared ? "요금제 변경 예약을 취소했습니다." : "요금제 변경을 예약했습니다.");
    await loadAccount();
  };

  const cancelSubscription = async () => {
    const endAt = formatDate(subscription?.access_end_at || subscription?.service_end_at);
    if (!window.confirm(`정기결제를 해지하시겠습니까?\n\n다음 자동결제는 중단되며 ${endAt}까지 이용할 수 있습니다. 이미 결제된 금액은 자동 환불되지 않습니다.`)) return;
    const result = await invoke("cancel_subscription");
    if (!result?.ok) return;
    setMessage(`정기결제를 해지했습니다. ${formatDate(result.accessEndAt)}까지 이용할 수 있습니다.`);
    await loadAccount();
  };

  return (
    <div className="space-y-1">
      <Header />
      <main className="px-4 pt-2 pb-6 space-y-5">
        <h1 className="text-lg font-semibold">내 계정</h1>
        {message && <p role="status" className="text-sm text-green-700">{message}</p>}
        {errorMessage && <p role="alert" className="text-sm text-red-700">{errorMessage}</p>}
        {loading ? <p className="text-sm text-gray-600">조회 중...</p> : (
          <>
            <section className="space-y-3" aria-labelledby="account-info-title">
              <h2 id="account-info-title" className="font-semibold">계정 정보</h2>
              <form onSubmit={saveProfile} className="grid max-w-4xl gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <label className="text-sm">회사명
                  <input className="mt-1 w-full border p-2 bg-gray-100" value={data.profile?.company_name || ""} readOnly />
                </label>
                <label className="text-sm">이메일
                  <input className="mt-1 w-full border p-2 bg-gray-100" value={data.profile?.email || ""} readOnly />
                </label>
                <label className="text-sm">사용자명
                  <input className="mt-1 w-full border p-2" value={form.driverName} maxLength={100}
                    onChange={(event) => setForm((current) => ({ ...current, driverName: event.target.value }))} required />
                </label>
                <label className="text-sm">전화번호
                  <input className="mt-1 w-full border p-2" value={form.phone} maxLength={30}
                    onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))} />
                </label>
                <label className="text-sm">직위
                  <input className="mt-1 w-full border p-2" value={form.position} maxLength={100}
                    onChange={(event) => setForm((current) => ({ ...current, position: event.target.value }))} />
                </label>
                <label className="text-sm">지점명
                  <input className="mt-1 w-full border p-2" value={form.branch} maxLength={100}
                    onChange={(event) => setForm((current) => ({ ...current, branch: event.target.value }))} />
                </label>
                <div className="flex items-end">
                  <button type="submit" disabled={!!busy} className="bg-blue-600 px-4 py-2 text-white disabled:opacity-50">
                    {busy === "update_profile" ? "저장 중..." : "수정 완료"}
                  </button>
                </div>
              </form>
            </section>

            <section className="space-y-3" aria-labelledby="subscription-title">
              <h2 id="subscription-title" className="font-semibold">결제 및 이용 현황</h2>
              {!subscription ? <p className="text-sm text-gray-600">등록된 이용권 결제 내역이 없습니다.</p> : (
                <>
                  <dl className="grid max-w-4xl grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
                    <div><dt className="text-gray-500">상태</dt><dd>{statusLabels[subscription.status] || subscription.status}</dd></div>
                    <div><dt className="text-gray-500">현재 요금제</dt><dd>{planLabels[subscription.plan] || subscription.plan}</dd></div>
                    <div><dt className="text-gray-500">결제 금액</dt><dd>{won(subscription.amount)}</dd></div>
                    <div><dt className="text-gray-500">결제 횟수</dt><dd>{subscription.paid_cycles || 0}회</dd></div>
                    <div><dt className="text-gray-500">최초 결제일</dt><dd>{formatDate(subscription.first_paid_at)}</dd></div>
                    <div><dt className="text-gray-500">다음 결제일</dt><dd>{formatDate(subscription.next_charge_at)}</dd></div>
                    <div><dt className="text-gray-500">현재 이용 가능일</dt><dd>{formatDate(subscription.access_end_at || subscription.service_end_at)}</dd></div>
                    <div><dt className="text-gray-500">변경 예정</dt><dd>{subscription.pending_plan ? `${planLabels[subscription.pending_plan]} (${formatDate(subscription.plan_change_effective_at)})` : "없음"}</dd></div>
                  </dl>
                  {active && (
                    <div className="flex max-w-4xl flex-wrap items-end gap-3">
                      <label className="text-sm">변경할 요금제
                        <select className="mt-1 block border p-2" value={targetPlan} onChange={(event) => setTargetPlan(event.target.value)}>
                          <option value="monthly">월간 선불 29,900원</option>
                          <option value="annual">연간 선불 300,000원</option>
                        </select>
                      </label>
                      <button type="button" disabled={!!busy} onClick={changePlan}
                        className="bg-blue-600 px-4 py-2 text-white disabled:opacity-50">
                        {targetPlan === subscription.plan ? "변경 예약 취소" : "결제 변경"}
                      </button>
                      <button type="button" disabled={!!busy} onClick={cancelSubscription}
                        className="bg-red-600 px-4 py-2 text-white disabled:opacity-50">
                        결제 취소(구독 해지)
                      </button>
                      <p className="basis-full text-sm text-gray-600">{changeSummary}</p>
                    </div>
                  )}
                  <p className="text-sm text-gray-600">
                    해지하면 다음 자동결제만 중단되고 결제된 이용기간까지 사용할 수 있습니다. 환불은
                    <a href="https://morbit.co.kr/refund/" target="_blank" rel="noreferrer" className="ml-1 text-blue-700 underline">환불정책</a>에 따라 별도로 처리됩니다.
                  </p>
                </>
              )}
            </section>

            <section className="space-y-2" aria-labelledby="payment-history-title">
              <h2 id="payment-history-title" className="font-semibold">결제 내역</h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border text-sm">
                  <thead className="bg-gray-100"><tr>
                    {["회차", "결제일", "요금제", "금액", "상태"].map((label) => <th key={label} className="border px-3 py-2 text-center">{label}</th>)}
                  </tr></thead>
                  <tbody>
                    {data.payments.length === 0 ? <tr><td colSpan={5} className="border p-4 text-center">결제 내역이 없습니다.</td></tr>
                      : data.payments.map((payment) => <tr key={payment.id}>
                        <td className="border px-3 py-2 text-center">{payment.cycle}</td>
                        <td className="border px-3 py-2 text-center whitespace-nowrap">{formatDate(payment.paid_at || payment.created_at, true)}</td>
                        <td className="border px-3 py-2 text-center">{planLabels[payment.plan] || "-"}</td>
                        <td className="border px-3 py-2 text-right">{won(payment.amount)}</td>
                        <td className="border px-3 py-2 text-center">{paymentStatusLabels[payment.status] || payment.status}</td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end pt-2">
                <Link to="/subscription" className="bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700">
                  결제 변경
                </Link>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
